import React, { useState, useEffect, useMemo } from 'react';
import {
  StarIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const sampleData = [
  { id: 'bitcoin', symbol: 'BTC', name: 'Bitcoin', price: 65432.10, change_24h: 2.34, volume: 28765432100, market_cap: 1287654321000, icon: '₿' },
  { id: 'ethereum', symbol: 'ETH', name: 'Ethereum', price: 3456.78, change_24h: -1.23, volume: 15678901234, market_cap: 415678901234, icon: 'Ξ' },
  { id: 'solana', symbol: 'SOL', name: 'Solana', price: 167.45, change_24h: 5.67, volume: 3456789012, market_cap: 74567890123, icon: '◎' },
  { id: 'xrp', symbol: 'XRP', name: 'XRP', price: 0.5678, change_24h: -0.45, volume: 1234567890, market_cap: 31234567890, icon: 'X' },
  { id: 'bnb', symbol: 'BNB', name: 'BNB', price: 567.89, change_24h: 1.23, volume: 987654321, market_cap: 87654321098, icon: 'B' },
  { id: 'cardano', symbol: 'ADA', name: 'Cardano', price: 0.4567, change_24h: 3.21, volume: 456789012, market_cap: 15987654321, icon: 'A' },
  { id: 'dogecoin', symbol: 'DOGE', name: 'Dogecoin', price: 0.1234, change_24h: -2.34, volume: 789012345, market_cap: 17654321098, icon: 'Ð' },
  { id: 'polkadot', symbol: 'DOT', name: 'Polkadot', price: 7.89, change_24h: 1.45, volume: 345678901, market_cap: 9876543210, icon: '●' },
  { id: 'avalanche', symbol: 'AVAX', name: 'Avalanche', price: 34.56, change_24h: 4.32, volume: 567890123, market_cap: 12345678901, icon: 'A' },
  { id: 'shiba', symbol: 'SHIB', name: 'Shiba Inu', price: 0.00002345, change_24h: -1.23, volume: 456789012, market_cap: 13876543210, icon: 'S' },
  { id: 'chainlink', symbol: 'LINK', name: 'Chainlink', price: 14.32, change_24h: 2.11, volume: 345678901, market_cap: 7890123456, icon: 'L' },
  { id: 'matic', symbol: 'MATIC', name: 'Polygon', price: 0.78, change_24h: 3.45, volume: 234567890, market_cap: 7234567890, icon: 'M' },
  { id: 'uniswap', symbol: 'UNI', name: 'Uniswap', price: 8.45, change_24h: -0.89, volume: 234567890, market_cap: 6345678901, icon: 'U' },
  { id: 'litecoin', symbol: 'LTC', name: 'Litecoin', price: 89.23, change_24h: 1.67, volume: 456789012, market_cap: 6678901234, icon: 'Ł' },
  { id: 'cosmos', symbol: 'ATOM', name: 'Cosmos', price: 9.87, change_24h: 2.34, volume: 234567890, market_cap: 3456789012, icon: 'C' },
];

const Markets = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [marketData, setMarketData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'market_cap', direction: 'desc' });

  useEffect(() => {
    const savedFavorites = localStorage.getItem('crypto_favorites');
    if (savedFavorites) setFavorites(JSON.parse(savedFavorites));
    setMarketData(sampleData);
    setLoading(false);
  }, []);

  const toggleFavorite = (assetId) => {
    let newFavorites;
    if (favorites.includes(assetId)) {
      newFavorites = favorites.filter((id) => id !== assetId);
    } else {
      newFavorites = [...favorites, assetId];
    }
    setFavorites(newFavorites);
    localStorage.setItem('crypto_favorites', JSON.stringify(newFavorites));
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'desc' ? 'asc' : 'desc',
    });
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '0.00';
    if (price < 0.01) return price.toFixed(8);
    if (price < 1) return price.toFixed(4);
    if (price < 1000) return price.toFixed(2);
    return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatMarketCap = (cap) => {
    if (!cap) return '$0';
    if (cap >= 1e12) return `$${(cap / 1e12).toFixed(2)}T`;
    if (cap >= 1e9) return `$${(cap / 1e9).toFixed(2)}B`;
    if (cap >= 1e6) return `$${(cap / 1e6).toFixed(2)}M`;
    return `$${cap.toLocaleString()}`;
  };

  const formatVolume = (vol) => {
    if (!vol) return '$0';
    if (vol >= 1e9) return `$${(vol / 1e9).toFixed(2)}B`;
    if (vol >= 1e6) return `$${(vol / 1e6).toFixed(2)}M`;
    return `$${vol.toLocaleString()}`;
  };

  const filteredData = useMemo(() => {
    let filtered = [...marketData];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (asset) =>
          asset.name?.toLowerCase().includes(query) ||
          asset.symbol?.toLowerCase().includes(query)
      );
    }

    switch (activeTab) {
      case 'favorites':
        filtered = filtered.filter((asset) => favorites.includes(asset.id));
        break;
      case 'gainers':
        filtered = filtered.filter((asset) => asset.change_24h > 0);
        break;
      case 'losers':
        filtered = filtered.filter((asset) => asset.change_24h < 0);
        break;
      default:
        break;
    }

    filtered.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (sortConfig.direction === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });

    return filtered;
  }, [marketData, searchQuery, activeTab, favorites, sortConfig]);

  const tabs = [
    { id: 'all', label: 'All Markets', icon: '📊' },
    { id: 'favorites', label: 'Favorites', icon: '⭐' },
    { id: 'gainers', label: 'Top Gainers', icon: '📈' },
    { id: 'losers', label: 'Top Losers', icon: '📉' },
  ];

  const SortArrow = ({ col }) => (
    <span className="ml-1 text-[11px] text-slate-500">
      {sortConfig.key === col ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
    </span>
  );

  if (loading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-slate-700 border-t-blue-500" />
          <p className="text-sm text-slate-400">Loading market data...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen p-4 text-white lg:p-6"
      style={{ background: 'linear-gradient(180deg, #0b1120 0%, #0f172a 100%)' }}
    >
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-white">Markets</h1>
          <p className="mt-1 text-sm text-slate-400">Track cryptocurrency prices and market activity</p>
        </div>

        <section className="mb-4 overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl shadow-black/20">
          <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full max-w-md">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-xl border border-slate-800 bg-slate-900 py-3 pl-10 pr-4 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-blue-500"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="rounded-xl border px-4 py-2.5 text-sm font-semibold transition"
                  style={{
                    background: activeTab === tab.id ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#0b1220',
                    color: activeTab === tab.id ? '#ffffff' : '#93c5fd',
                    borderColor: activeTab === tab.id ? '#60a5fa' : '#1e3a8a',
                    boxShadow: activeTab === tab.id ? '0 8px 20px rgba(37,99,235,0.25)' : 'none'
                  }}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl shadow-black/20">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-900/80">
                <tr className="border-b border-slate-800 text-xs uppercase tracking-wide text-slate-500">
                  <th className="w-12 px-4 py-4 text-center"></th>
                  <th className="px-4 py-4 text-left">Asset</th>
                  <th
                    className="cursor-pointer px-4 py-4 text-right transition hover:text-white"
                    onClick={() => handleSort('price')}
                  >
                    Price <SortArrow col="price" />
                  </th>
                  <th
                    className="cursor-pointer px-4 py-4 text-right transition hover:text-white"
                    onClick={() => handleSort('change_24h')}
                  >
                    24h Change <SortArrow col="change_24h" />
                  </th>
                  <th
                    className="hidden cursor-pointer px-4 py-4 text-right transition hover:text-white md:table-cell"
                    onClick={() => handleSort('volume')}
                  >
                    Volume <SortArrow col="volume" />
                  </th>
                  <th
                    className="hidden cursor-pointer px-4 py-4 text-right transition hover:text-white lg:table-cell"
                    onClick={() => handleSort('market_cap')}
                  >
                    Market Cap <SortArrow col="market_cap" />
                  </th>
                  <th className="px-4 py-4 text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center text-slate-500">
                      {activeTab === 'favorites'
                        ? 'No favorites yet. Click the star icon to add assets.'
                        : 'No assets found.'}
                    </td>
                  </tr>
                ) : (
                  filteredData.map((asset) => {
                    const positive = asset.change_24h >= 0;
                    return (
                      <tr
                        key={asset.id}
                        className="border-b border-slate-900 transition hover:bg-slate-900/50"
                      >
                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => toggleFavorite(asset.id)}
                            className="rounded-lg p-2 transition hover:bg-slate-800"
                            type="button"
                          >
                            {favorites.includes(asset.id) ? (
                              <StarIconSolid className="h-4 w-4 text-yellow-400" />
                            ) : (
                              <StarIcon className="h-4 w-4 text-slate-500 hover:text-yellow-400" />
                            )}
                          </button>
                        </td>

                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/15 text-sm font-bold text-blue-300 ring-1 ring-blue-500/20">
                              {asset.icon}
                            </div>
                            <div>
                              <p className="font-semibold text-white">{asset.name}</p>
                              <p className="text-xs uppercase tracking-wide text-slate-500">{asset.symbol}</p>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-4 text-right font-mono text-sm font-semibold text-white">
                          ${formatPrice(asset.price)}
                        </td>

                        <td className="px-4 py-4 text-right">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                              positive
                                ? 'bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/20'
                                : 'bg-red-500/10 text-red-400 ring-1 ring-red-500/20'
                            }`}
                          >
                            {positive ? (
                              <ChevronUpIcon className="h-3 w-3" />
                            ) : (
                              <ChevronDownIcon className="h-3 w-3" />
                            )}
                            {Math.abs(asset.change_24h).toFixed(2)}%
                          </span>
                        </td>

                        <td className="hidden px-4 py-4 text-right text-sm text-slate-400 md:table-cell">
                          {formatVolume(asset.volume)}
                        </td>

                        <td className="hidden px-4 py-4 text-right text-sm text-slate-400 lg:table-cell">
                          {formatMarketCap(asset.market_cap)}
                        </td>

                        <td className="px-4 py-4 text-center">
                          <button
                            type="button"
                            className="rounded-xl border px-4 py-2 text-sm font-semibold transition"
                            style={{
                              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                              color: '#fff',
                              borderColor: '#60a5fa',
                              boxShadow: '0 8px 20px rgba(37,99,235,0.20)'
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div className="mt-4 text-xs text-slate-500">
          Market data is for informational purposes only.
        </div>
      </div>
    </div>
  );
};

export default Markets;